import { useEffect, useState } from "react";
import { Configuration } from "../context/ConfigurationContext";
import { useConnection } from "../context/ConnectionContext";
import { Pitch } from "../types/StrikeZone";
import { getExitVelocity, getLaunchAngle, getPitchType, getSpeed, InformationLabels } from "../util/InformationUtil";

export type useSlimInformationType = {
  primary: string;
  primaryLabel: string;
  secondary: string;
  secondaryLabel: string;
};

const seperator = "  /  ";

const useSlimInformation = (config: Configuration): useSlimInformationType => {
  const { pitch } = useConnection();

  const [primary, setPrimary] = useState<string>("");
  const [primaryLabel, setPrimaryLabel] = useState<string>("");
  const [secondary, setSecondary] = useState<string>("");
  const [secondaryLabel, setSecondaryLabel] = useState<string>("");

  const setBasePitchData = (config: Configuration, pitch: Pitch) => {
    setPrimary("");
    setSecondary("");
    if (config.display.showPitchSpeed) {
      setSecondary(getSpeed(pitch));
      setSecondaryLabel(InformationLabels.pitchSpeed);
    }
    if (config.display.showPitchType) {
      setPrimary(getPitchType(pitch));
      setPrimaryLabel(InformationLabels.pitchType);
    }
  };

  const resetData = (_config: Configuration) => {
    setPrimary(InformationLabels.emptyData);
    setSecondary(InformationLabels.emptyData);
  };

  const setupHitDataValues = (hitData: string[], hitLabels: string[], pitchData: string[], pitchLabels: string[]) => {
    if (pitchData.length > hitData.length) {
      setPrimaryLabel(hitLabels.join(seperator));
      setPrimary(hitData.join(seperator));
      setSecondaryLabel(pitchLabels.join(seperator));
      setSecondary(pitchData.join(seperator));
    } else {
      setPrimaryLabel(pitchLabels.join(seperator));
      setPrimary(pitchData.join(seperator));
      setSecondaryLabel(hitLabels.join(seperator));
      setSecondary(hitData.join(seperator));
    }
  };

  useEffect(() => {
    if (!!pitch) {
      if (pitch.inPlay && (config.display.showExitVelocity || !!pitch.exitVelocity)) {
        if (config.display.showPitchType || config.display.showPitchSpeed) {
          const hitData: string[] = [];
          const hitLabels: string[] = [];
          const pitchData: string[] = [];
          const pitchLabels: string[] = [];
          if (config.display.showExitVelocity && !!pitch.exitVelocity) {
            hitData.push(getExitVelocity(pitch));
            hitLabels.push(InformationLabels.exitVelocity);
          }
          if (config.display.showLaunchAngle && !!pitch.launchAngle) {
            hitData.push(getLaunchAngle(pitch));
            hitLabels.push(InformationLabels.launchAngle);
          }
          if (config.display.showPitchSpeed) {
            pitchData.push(getSpeed(pitch));
            pitchLabels.push(InformationLabels.pitchSpeed);
          }
          if (config.display.showPitchType) {
            pitchData.push(getPitchType(pitch));
            pitchLabels.push(InformationLabels.pitchType);
          }
          setupHitDataValues(hitData, hitLabels, pitchData, pitchLabels);
        } else {
          if (config.display.showExitVelocity && config.display.showLaunchAngle) {
            setPrimary(getExitVelocity(pitch));
            setPrimaryLabel(InformationLabels.exitVelocity);
            setSecondary(getLaunchAngle(pitch));
            setSecondaryLabel(InformationLabels.launchAngle);
          } else if (config.display.showLaunchAngle) {
            setPrimary(getLaunchAngle(pitch));
            setPrimaryLabel(InformationLabels.launchAngle);
          } else if (config.display.showExitVelocity) {
            setPrimary(getExitVelocity(pitch));
            setPrimaryLabel(InformationLabels.exitVelocity);
          }
        }
      } else {
        setBasePitchData(config, pitch);
      }
    } else {
      resetData(config);
    }
  }, [pitch, config.display]);

  return {
    primary,
    primaryLabel,
    secondary,
    secondaryLabel,
  };
};

export default useSlimInformation;
